import { isWizard } from './wizard';

export function isNode(
  item: Elvita.Node | Elvita.WizardItem,
): item is Elvita.Node {
  return 'topicId' in item;
}

function getNodeId(node: Elvita.Node | Elvita.WizardItem): string {
  return isWizard(node) ? node.id : node.topicId;
}

function getNodeTitle(node: Elvita.Node | Elvita.WizardItem): string {
  return isWizard(node) ? node.id : node.title;
}

function buildLink(
  hasChildren: boolean,
  topLevel: string,
  nodeId: string,
  child: Elvita.Node | Elvita.WizardItem,
): string {
  if (isWizard(child)) {
    return `/wizard/${topLevel}/${nodeId}`;
  }
  return hasChildren
    ? `/browse/${topLevel}/${nodeId}`
    : `/${topLevel}/topics/${nodeId}`;
}

function getCrumbs(
  child: Elvita.Node | Elvita.WizardItem,
  topicId: string,
  topLevel: string,
) {
  const breadCrumbTitle = isWizard(child)
    ? child.id
    : child.title?.replace(/ /g, '');
  const title = getNodeTitle(child);
  const nodeId = getNodeId(child);

  const breadcrumbs = {
    [breadCrumbTitle]: [
      {
        label: title,
        url: buildLink(
          (child.children?.length ?? 0) > 0,
          topLevel,
          nodeId,
          child,
        ),
      },
    ],
  };

  let currentNode: Elvita.Node | Elvita.WizardItem | undefined =
    getNodeId(child) === topicId ? child : undefined;

  function recurse(childNode: Elvita.Node | Elvita.WizardItem) {
    const children = childNode.children;
    if (!children) return;
    const childNodeTitle = getNodeTitle(childNode);

    for (const currentChild of children) {
      const childNodeId = getNodeId(currentChild);

      if (childNodeId === topicId) {
        currentNode = currentChild;

        if (!breadcrumbs[breadCrumbTitle].find(b => b.label === childNodeTitle)) {
          breadcrumbs[breadCrumbTitle].push({
            label: childNodeTitle,
            url: childNode.children
              ? `/browse/${topLevel}/${childNodeId}`
              : `/${topLevel}/topics/${childNodeId}`,
          });
        }

        breadcrumbs[breadCrumbTitle].push({
          label: getNodeTitle(currentChild),
          url: currentChild.children
            ? `/browse/${topLevel}/${childNodeId}`
            : `/${topLevel}/topics/${childNodeId}`,
        });
      }

      recurse(currentChild);
    }
  }

  // Only recurse if child has children and isn't itself the target node
  if (child.children && getNodeId(child) !== topicId) {
    recurse(child);
  }

  return { breadcrumbs: breadcrumbs[breadCrumbTitle], currentNode };
}

type GetChildrenResult = {
  nodeData: Elvita.Node | Elvita.WizardItem | undefined;
  breadcrumbs: Elvita.BreadCrumb[];
};

export function getChildren(
  data: Elvita.Node | Elvita.WizardItem,
  childId: string,
  topLevel: string,
): GetChildrenResult {
  let node: GetChildrenResult = {
    nodeData: undefined,
    breadcrumbs: [],
  };

  data.children?.forEach(child => {
    const { currentNode, breadcrumbs } = getCrumbs(child, childId, topLevel);
    if (currentNode) {
      node = { nodeData: currentNode, breadcrumbs };
    }
  });

  return node;
}

export function getSiblings(
  currentTopicId: string,
  browseList: Elvita.Node,
): Elvita.Node[] | [] {
  if (!browseList || !browseList.children || browseList.children.length < 1) {
    return [];
  }

  const siblings: Elvita.Node[] = [];

  const findSiblings = (
    currentTopicId: string,
    browseList: Elvita.Node,
  ): void => {
    const children = browseList.children;
    if (!children) return;

    children.forEach(child => {
      if (child && child.children) {
        findSiblings(currentTopicId, child);
      }

      if (child.topicId === currentTopicId) {
        siblings.push(...children);
      }
    });
  };

  findSiblings(currentTopicId, browseList);
  return siblings;
}

export function getCurrentBrowseItem(
  browseList: Elvita.Node[],
  currentId: string,
): {
  topLevelItem: Elvita.Node | undefined;
  currentBrowseItem: Elvita.Node | undefined;
} {
  if (!browseList) {
    return { topLevelItem: undefined, currentBrowseItem: undefined };
  }
  const list = browseList[0]?.children ?? [];
  let currentBrowseItem: Elvita.Node | undefined = undefined;
  let topLevelItem: Elvita.Node | undefined = undefined;

  list.forEach(topItem => {
    const searchBrowseTree = (browse: Elvita.Node[]): void => {
      browse.forEach(entry => {
        if (entry.topicId === currentId) {
          topLevelItem = topItem;
          currentBrowseItem = entry;
        } else {
          if (entry.children) {
            searchBrowseTree(entry.children);
          }
        }
      });
    };

    if (topItem.children) {
      searchBrowseTree(topItem.children);
    }
  });

  return { topLevelItem, currentBrowseItem };
}

export function findPathToTopic(
  node: Elvita.Node,
  topicId: string,
  path: Elvita.Node[] = [],
): Elvita.Node[] {
  if (!node) return [];
  if (node.topicId === topicId) return [...path, node];
  if (!node.children) return [];

  for (const child of node.children) {
    const result = findPathToTopic(child, topicId, [...path, node]);
    if (result.length) return result;
  }
  return [];
}
